import _ from 'lodash';
import React, { Dispatch, SetStateAction } from 'react';
import { GUTTER } from './PanZoomImage';

type SetStateFunction<T> = (prev: T) => T;

interface Point {
  x: number;
  y: number;
}

interface ZoomHook {
  imageSize: { width: number; height: number };
  setImageSize: Dispatch<SetStateAction<{ width: number; height: number }>>;
  ctxWidth: number;
  ctxHeight: number;
  imgWidth: number;
  imgHeight: number;
  ratioWidth: number;
  ratioHeight: number;
  ratioAspect: number;
  newWidth: number;
  newHeight: number;
  offsetX: number;
  offsetY: number;
  zoom: number;
  setZoom: Dispatch<SetStateAction<number>>;
  fixPan: (fromZoom: number, toZoom: number, focusPoint?: Point) => void;
  setZoomPan: (getZoom: SetStateFunction<number>, focusPoint?: Point) => void;
  onZoomIncrease: () => void;
  onZoomDecrease: () => void;
  onDoubleClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  formatPercent: (value: number) => string;
  Minus: () => JSX.Element;
  Plus: () => JSX.Element;
  getZoomLevelDecrease: (zoom: number) => number;
  getZoomLevelIncrease: (zoom: number) => number;
  zoomLevels: number[];
}

export interface ZoomHookProps {
  size: { width: number; height: number };
  canvas?: React.MutableRefObject<HTMLDivElement | null>;
}
export const useZoomHook = (props: ZoomHookProps): ZoomHook => {
  const clientWidth = props.size.width - GUTTER * 2;
  const clientHeight = props.size.height - GUTTER * 2;
  const [imageSize, setImageSize] = React.useState({ width: 1, height: 1 });
  const [zoom, setZoom] = React.useState<number>(1);
  const ctxWidth = clientWidth * zoom;
  const ctxHeight = clientHeight * zoom;
  const imgWidth = imageSize.width;
  const imgHeight = imageSize.height;
  const ratioWidth = imgWidth / ctxWidth;
  const ratioHeight = imgHeight / ctxHeight;
  const ratioAspect = Math.max(ratioWidth, ratioHeight);
  const newWidth = imgWidth / ratioAspect;
  const newHeight = imgHeight / ratioAspect;
  const offsetX = ctxWidth / 2 - newWidth / 2;
  const offsetY = ctxHeight / 2 - newHeight / 2;

  const fixPan = function (fromZoom: number, toZoom: number, focusPoint?: Point): void {
    let pctX = 0.5;
    let pctY = 0.5;
    if (focusPoint) {
      pctX = focusPoint.x / ctxWidth;
      pctY = focusPoint.y / ctxHeight;
    }
    const dx = clientWidth * (toZoom - fromZoom) * pctX;
    const dy = clientHeight * (toZoom - fromZoom) * pctY;
    setTimeout(() => {
      if (props.canvas && !props.canvas.current) {
        return;
      }
      if (props.canvas && props.canvas.current) {
        props.canvas.current.scrollLeft += Math.round(dx);
        props.canvas.current.scrollTop += Math.round(dy);
      }
    }, 0);
  };

  const setZoomPan = function (getZoom: SetStateFunction<number>, focusPoint?: Point): void {
    setZoom(lastZoom => {
      const nextZoom = getZoom(lastZoom);
      fixPan(lastZoom, nextZoom, focusPoint);
      return nextZoom;
    });
  };

  const onZoomIncrease = () => {
    setZoomPan(getZoomLevelIncrease);
  };

  const onZoomDecrease = () => {
    setZoomPan(getZoomLevelDecrease);
  };

  const onDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!props.canvas!.current) {
      onZoomIncrease();
      return;
    }
    const offset = props.canvas!.current.getBoundingClientRect();
    const focusPoint: Point = {
      x: event.pageX - offset.left,
      y: event.pageY - offset.top,
    };
    setZoomPan(getZoomLevelIncrease, focusPoint);
  };

  const formatPercent = function (pct: number): string {
    return Math.round(pct * 100).toString(10) + '%';
  };

  const Minus = function () {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12.8 6.7998H3.19999C2.75824 6.7998 2.39999 7.15805 2.39999 7.5998V8.3998C2.39999 8.84155 2.75824 9.1998 3.19999 9.1998H12.8C13.2417 9.1998 13.6 8.84155 13.6 8.3998V7.5998C13.6 7.15805 13.2417 6.7998 12.8 6.7998Z"
          fill="black"
        />
      </svg>
    );
  };

  const Plus = function () {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12.8 6.80039H9.19999V3.20039C9.19999 2.75864 8.84174 2.40039 8.39999 2.40039H7.59999C7.15824 2.40039 6.79999 2.75864 6.79999 3.20039V6.80039H3.19999C2.75824 6.80039 2.39999 7.15864 2.39999 7.60039V8.40039C2.39999 8.84214 2.75824 9.20039 3.19999 9.20039H6.79999V12.8004C6.79999 13.2421 7.15824 13.6004 7.59999 13.6004H8.39999C8.84174 13.6004 9.19999 13.2421 9.19999 12.8004V9.20039H12.8C13.2417 9.20039 13.6 8.84214 13.6 8.40039V7.60039C13.6 7.15864 13.2417 6.80039 12.8 6.80039Z"
          fill="black"
        />
      </svg>
    );
  };

  const zoomLevels = _.range(1, 101);

  const getZoomLevelDecrease = function (zoom: number): number {
    for (let i = zoomLevels.length - 1; i >= 0; i--) {
      if (zoomLevels[i] < zoom) {
        return zoomLevels[i];
      }
    }
    return zoom;
  };

  const getZoomLevelIncrease = function (zoom: number): number {
    for (const zoomLevel of zoomLevels) {
      if (zoomLevel > zoom) {
        return zoomLevel;
      }
    }
    return zoom;
  };

  return {
    imageSize,
    setImageSize,
    ctxWidth,
    ctxHeight,
    imgWidth,
    imgHeight,
    ratioWidth,
    ratioHeight,
    ratioAspect,
    newWidth,
    newHeight,
    offsetX,
    offsetY,
    zoom,
    setZoom,
    fixPan,
    setZoomPan,
    onZoomIncrease,
    onZoomDecrease,
    onDoubleClick,
    formatPercent,
    Minus,
    Plus,
    getZoomLevelDecrease,
    getZoomLevelIncrease,
    zoomLevels,
  };
};
